import type { FirebaseOptions } from 'firebase/app'
import { initializeApp } from 'firebase/app'
import type { User } from 'firebase/auth'
import {
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  type Auth,
} from 'firebase/auth'
import {
  connectFirestoreEmulator,
  initializeFirestore,
  type Firestore,
} from 'firebase/firestore'
import {
  connectFunctionsEmulator,
  getFunctions,
  type Functions,
} from 'firebase/functions'
import {
  connectStorageEmulator,
  getDownloadURL,
  getStorage,
  ref,
  type FirebaseStorage,
} from 'firebase/storage'
import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  when,
} from 'mobx'
import { UnsubscribeManager } from '../util/UnsubscribeManager'
import { BreakoutUser } from './BreakoutUser'
import { GlobalAuthorStore } from '../stores/GlobalAuthorStore'
import { UserStore } from '../stores/UserStore'
import { DateTime } from 'luxon'
import { browserLocalPersistence, setPersistence } from 'firebase/auth'
import type { SlideDeckMaterial } from './SlideDeckMaterial'
import {
  getAnalytics,
  logEvent,
  setUserId,
  type Analytics,
} from 'firebase/analytics'
import { decodeJwt } from 'jose'
import * as Sentry from '@sentry/core'
import { EventEmitter } from '../util/EventEmitter'
import type { StreamSubscriptionActions } from 'tricklejs/dist/stream_subscription'
import { getSettingsFeatureFlags } from '../firestore/SettingsFeatureFlags'
import { SettingsFeatureFlags } from './SettingsFeatureFlags'
import { getSettingsFeatureFlagsBeta } from '../firestore/SettingsFeatureFlagsBeta'
import { getSettingsPricing } from '../firestore/SettingsPricing'
import { SettingsPricing } from './SettingsPricing'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { SettingsSectionPassPricing } from './SettingsSectionPassPricing'
import { fetchCSVFieldLabels } from '../firestore/CSVHeaders/index'
import { httpsCallable } from 'firebase/functions'
import { Section } from './Section'
import { PublicUser } from './PublicUser'
import type { CheckIsValidInvitationResult } from '../types'
import type {
  GetSlideDeckForAssignmentResult,
  SlideDeckInclude,
} from '../types/slideDeckAccess'
import { RubricTypeCubit } from '../cubits/RubricTypeCubit'
import { SettingsKillSwitch } from './SettingsKillSwitch'
import { getSettingsKillSwitch } from '../firestore/SettingsKillSwitch'
import { SettingsDeadAirDetector } from './SettingsDeadAirDetector'
import { getSettingsDeadAirDetector } from '../firestore/SettingsDeadAirDetector'
import { FirebaseRepositoryUtils } from './FirebaseRepositoryUtils'
import {
  resolveLocale,
  supportedLocales,
  type SupportedLocale,
} from '../util/locale'

export type BuildOptions = {
  environment: string
  firebase: FirebaseOptions
  useLocalPersistence?: boolean
  disableAnalytics?: boolean
  inBeta?: boolean
}

export type FirebaseRepositoryEvents = {
  logout: []
}

// TODO: move to stores
// Single class housing all Firebase services
export class FirebaseRepository {
  firestore: Firestore
  auth: Auth
  storage: FirebaseStorage
  functions: Functions
  analytics?: Analytics
  initialized = false
  inBeta = false
  environment: string
  databaseId?: string

  // This is a testing only flag
  _testCrashApp = observable.box(false)

  currentUser: User | null = null
  breakoutUser: BreakoutUser | null = null

  supportedLocales: readonly SupportedLocale[] = supportedLocales
  locale: SupportedLocale = 'en'

  _authToken: string | null = null
  _authTokenRefreshTimeout: NodeJS.Timeout | undefined = undefined
  _featureFlagStream: StreamSubscriptionActions | undefined = undefined
  _killSwitchStream: StreamSubscriptionActions | undefined = undefined
  _deadAirSettingsStream: StreamSubscriptionActions | undefined = undefined
  _featureFlags: SettingsFeatureFlags
  _killSwitch: SettingsKillSwitch
  _deadAirSettings: SettingsDeadAirDetector
  _pricing: SettingsPricing
  _pricingStream: StreamSubscriptionActions | undefined = undefined
  _sectionPassPricingStream: StreamSubscriptionActions | undefined = undefined
  _sectionPassPricing: SettingsSectionPassPricing

  rubricTypes: RubricTypeCubit

  // with observables, relying on time is a bit tricky
  // so we have a separate observable for the current minute
  // which is updated every minute
  // A minute, because we don't need to be more granular than that
  currentMinute: DateTime = DateTime.now()
  _currentMinuteInterval: NodeJS.Timeout | null = null

  utils: FirebaseRepositoryUtils = new FirebaseRepositoryUtils(this)

  /**
   * prevent user from mounting the AuthedEntrypoint
   * currently used to block re-direct when user is
   * linking 2nd auth method on the loginPAge
   */
  entrypointLocked: boolean = false

  globalAuthorStore: GlobalAuthorStore = new GlobalAuthorStore(this)
  userStore: UserStore = new UserStore(this)

  numberStore = observable.map<string, number>({})

  unsubscribers = new UnsubscribeManager()
  events = new EventEmitter<FirebaseRepositoryEvents>()

  hasZodError: boolean = false

  static build(options: BuildOptions) {
    const app = initializeApp(options.firebase)
    const auth = getAuth(app)
    const databaseId = localStorage.getItem('firestore-databaseId')
    let firestore: Firestore
    if (databaseId) {
      firestore = initializeFirestore(
        app,
        { ignoreUndefinedProperties: true },
        databaseId
      )
    } else {
      firestore = initializeFirestore(app, { ignoreUndefinedProperties: true })
    }
    const storage = getStorage(app)
    const functions = getFunctions(app)
    const analytics = options.disableAnalytics ? undefined : getAnalytics(app)

    if (options.useLocalPersistence) {
      setPersistence(auth, browserLocalPersistence)
    }

    const repository = new FirebaseRepository(
      firestore,
      auth,
      storage,
      functions,
      options.environment,
      analytics
    )

    if (options.inBeta) repository.inBeta = true
    if (databaseId) repository.databaseId = databaseId

    return repository
  }

  constructor(
    firestore: Firestore,
    auth: Auth,
    storage: FirebaseStorage,
    functions: Functions,
    environment: string,
    analytics?: Analytics
  ) {
    this.firestore = firestore
    this.auth = auth
    this.storage = storage
    this.functions = functions
    this.analytics = analytics
    this.environment = environment

    this.breakoutUser = null

    this._featureFlags = SettingsFeatureFlags.empty(this)
    this._pricing = SettingsPricing.empty(this)
    this._killSwitch = SettingsKillSwitch.empty(this)
    this._deadAirSettings = SettingsDeadAirDetector.empty(this)
    this._sectionPassPricing = SettingsSectionPassPricing.empty(this)

    this.rubricTypes = new RubricTypeCubit(this)

    makeObservable(this, {
      // observables
      _authToken: observable,
      entrypointLocked: observable,
      currentUser: observable,
      initialized: observable,
      currentMinute: observable,
      breakoutUser: observable,
      locale: observable,
      hasZodError: observable,

      // actions
      update: action,
      finalizeInitialization: action,
      updateCurrentMinute: action,
      lockEntrypoint: action,
      unlockEntrypoint: action,
      setLocale: action,
      testCrashApp: action,
      triggerOnZodError: action,

      // computed
      uid: computed,
      isAuthenticated: computed,
      authToken: computed,
      shouldTestCrashApp: computed,
    })
  }

  initialize() {
    this.startMinuteTicker()
    this.unsubscribers.add(
      onAuthStateChanged(this.auth, async (user) => {
        // Force a token refresh before setting the user. When onAuthStateChanged
        // fires, Firebase Auth knows about the user but the auth token may not
        // yet be propagated to Firestore. Setting the user immediately triggers
        // MobX reactions that start Firestore streams (e.g. getUserProfile,
        // getActiveGradeSyncJobs, getPeerReviewNotesForRevieweeInRoom), which
        // fail with "Missing or insufficient permissions" because Firestore
        // security rules can't validate the not-yet-propagated token.
        // By awaiting getIdToken(true) first, we ensure the token is valid and
        // available to Firestore before any streams are started.
        // See: CASES-REACT-2PY, CASES-REACT-2Y3, CASES-REACT-283
        if (user) await user.getIdToken(true)
        this.update({ user })
        if (!this.initialized) this.finalizeInitialization()
      })
    )

    // fetch/refresh the auth token when currentUser changes
    this.unsubscribers.add(
      reaction(
        () => this.currentUser,
        async () => {
          this.refreshAuthToken()
        },
        { fireImmediately: true }
      )
    )

    return this
  }

  fetchCSVFieldLabels = async () => {
    return await fetchCSVFieldLabels(this)
  }

  lockEntrypoint = () => {
    this.entrypointLocked = true
  }

  unlockEntrypoint = () => {
    this.entrypointLocked = false
  }

  get inProduction() {
    return this.environment === 'production'
  }

  get inProductionOrBeta() {
    return this.environment === 'production' || this.environment === 'beta'
  }

  setLocale = (locale: string) => {
    this.locale = resolveLocale(locale)
  }

  onLogout(callback: () => void) {
    this.events.addListener('logout', callback)
  }

  offLogout(callback: () => void) {
    this.events.removeListener('logout', callback)
  }

  get featureFlags() {
    this.startFeatureFlagStreamIfNotRunning()
    return this._featureFlags
  }

  get killSwitch() {
    this.startKillSwitchStreamIfNotRunning()
    return this._killSwitch
  }

  get deadAirSettings() {
    this.startDeadAirSettingsStreamIfNotRunning()
    return this._deadAirSettings
  }

  get pricing() {
    this.startPricingStreamIfNotRunning()
    return this._pricing
  }

  get sectionPassPricing() {
    this.startSectionPassPricingStreamIfNotRunning()
    return this._sectionPassPricing
  }

  startFeatureFlagStreamIfNotRunning() {
    if (this._featureFlagStream) return

    const stream = this.inBeta
      ? getSettingsFeatureFlagsBeta(this)
      : getSettingsFeatureFlags(this)
    this._featureFlagStream = stream.listen((model) => {
      // should feature flags fail to load, we don't want to block the app
      // use the defaults
      if (!model.data) {
        this._featureFlags.changeLoadingState(false)
        return
      }
      this._featureFlags.replaceModel(model)
    })
  }

  startPricingStreamIfNotRunning() {
    if (!this.uid) return
    if (this._pricingStream) return

    this._pricingStream = getSettingsPricing(this).listen((model) => {
      this._pricing.replaceModel(model)
    })
  }

  startSectionPassPricingStreamIfNotRunning() {
    if (this._sectionPassPricingStream) return

    this._sectionPassPricingStream = getSettingsSectionPassPricing(this).listen(
      (model) => {
        this._sectionPassPricing.replaceModel(model)
      }
    )
  }

  startKillSwitchStreamIfNotRunning() {
    if (this._killSwitchStream) return

    this._killSwitchStream = getSettingsKillSwitch(this).listen((model) => {
      this.killSwitch.replaceModel(model)
    })
  }

  startDeadAirSettingsStreamIfNotRunning() {
    if (this._deadAirSettingsStream) return

    this._deadAirSettingsStream = getSettingsDeadAirDetector(this).listen(
      (model) => {
        this._deadAirSettings.replaceModel(model)
      }
    )
  }

  logEvent(name: string, params?: Record<string, unknown>) {
    // Test tap, before the analytics guard rather than after it. Analytics is
    // constructed with `disableAnalytics: inE2E()`, so under Playwright this
    // method returns on the next line and every event the app emits is
    // unobservable — including the ones that are the only record of what the
    // dead-air detector decided. Nothing installs the tap outside a test; a
    // browser that has not had one written onto it takes the same two lines it
    // always did.
    const tap = (
      globalThis as {
        __breakoutLogEventTap?: (
          name: string,
          params?: Record<string, unknown>
        ) => void
      }
    ).__breakoutLogEventTap
    if (tap) {
      try {
        tap(name, params)
      } catch {
        // A broken tap is a broken test, not a broken room.
      }
    }
    if (this.analytics === undefined) return
    logEvent(this.analytics, name, params)
  }

  // Single entry point for the getSlideDeckForAssignment callable. Every caller
  // routes through here so there is one place to reason about (and instrument)
  // this call. `callsite` and `reason` are forwarded to the server and logged
  // there, so we can see exactly where and why each invocation originated while
  // we track down what drives the call volume.
  async fetchSlideDeckForAssignment(params: {
    sectionId?: string
    assignmentId?: string
    roomId?: string
    slideDeckId?: string
    include: SlideDeckInclude[]
    callsite: string
    reason: string
    // Optional diagnostics: the calling cubit instance id (to spot re-create
    // loops) and its in-memory room selection (may differ from the URL).
    instanceId?: string
    clientRoomStateId?: string
  }): Promise<GetSlideDeckForAssignmentResult> {
    const callable = httpsCallable<
      {
        sectionId?: string
        assignmentId?: string
        roomId?: string
        slideDeckId?: string
        include: SlideDeckInclude[]
        callsite: string
        reason: string
        instanceId?: string
        clientRoomStateId?: string
        databaseId?: string
      },
      GetSlideDeckForAssignmentResult
    >(this.functions, 'getSlideDeckForAssignment')

    const { data } = await callable({
      ...params,
      ...(this.databaseId && { databaseId: this.databaseId }),
    })
    return data
  }

  setAnalyticsUserId(userId: string | null) {
    if (this.analytics === undefined) return
    setUserId(this.analytics, userId)
  }

  startMinuteTicker() {
    const firstTargetTime = DateTime.now().endOf('minute').plus({ seconds: 1 }) // The next time of the form XX:YY:01, but not less than 1 second (for stability reasons)
    const firstDelayMs = firstTargetTime.diffNow().toMillis()

    const regularIntervalMs = 60 * 1000

    setTimeout(() => {
      this.updateCurrentMinute()
      this._currentMinuteInterval = setInterval(() => {
        this.updateCurrentMinute()
      }, regularIntervalMs)
    }, firstDelayMs)
  }

  updateCurrentMinute() {
    this.currentMinute = DateTime.now()
  }

  finalizeInitialization() {
    this.initialized = true
  }

  dispose() {
    this._currentMinuteInterval && clearInterval(this._currentMinuteInterval)
    this.unsubscribers.dispose()
  }

  connectToEmulator(host?: string) {
    const emulatorHost = host || 'localhost'

    // skip if we're already using the emulator in the browser
    // hot reload can cause this to be called multiple times
    if (this.auth.emulatorConfig) return

    connectAuthEmulator(this.auth, `http://${emulatorHost}:9099`, {
      disableWarnings: true,
    })
    connectFirestoreEmulator(this.firestore, emulatorHost, 8080)
    connectStorageEmulator(this.storage, emulatorHost, 9199)
    connectFunctionsEmulator(this.functions, emulatorHost, 5001)
  }

  update(data: { user: User | null }) {
    if (data.user !== undefined) {
      this.currentUser = data.user

      if (data.user) {
        this.setAnalyticsUserId(data.user.uid)
        this.breakoutUser = new BreakoutUser(this, data.user.uid)
        this.breakoutUser.initialize()

        // we do this on login, its not in the initialize because it is only safe to call
        // if user is currently authed as the user represented by the class
        // that is always true here on login
        this.breakoutUser.syncProfileEmailToAuthValue()

        Sentry.setUser({ id: data.user.uid })
      } else {
        this.logout()
        this.setAnalyticsUserId(null)
        this.breakoutUser = new BreakoutUser(this, '')
        Sentry.setUser(null)
      }
    }
  }

  get authToken() {
    // check if the token is expired as a fail safe in case the token is not refreshed
    if (this._authToken) {
      const { exp } = decodeJwt(this._authToken)
      // should only occur if the 10 minute refresh failed
      const nowMinusFiveMinutes = DateTime.now()
        .minus({ minutes: 5 })
        .toSeconds()
      if (exp && exp < nowMinusFiveMinutes) this.refreshAuthToken(true)
    }
    return this._authToken
  }

  get uid() {
    return this.breakoutUser?.uid || ''
  }

  get isAuthenticated() {
    return this.currentUser !== null
  }

  logout() {
    // we're logging out, dispose of the user
    this.breakoutUser?.dispose()
    // clear the user store and cancel subscription
    this.userStore.clear()
    this.globalAuthorStore.clear()

    this.events.emit('logout')

    if (this._featureFlagStream) {
      this._featureFlagStream.cancel()
      this._featureFlagStream = undefined
    }

    if (this._pricingStream) {
      this._pricingStream?.cancel()
      this._pricingStream = undefined
    }

    if (this._sectionPassPricingStream) {
      this._sectionPassPricingStream.cancel()
      this._sectionPassPricingStream = undefined
    }

    if (this._killSwitchStream) {
      this._killSwitchStream.cancel()
      this._killSwitchStream = undefined
    }

    // Left running, this one errors: `settings/dead_air_detector` is gated on
    // isSignedIn(), so the live listener takes a permission-denied the moment
    // auth clears, and its stream reports it to Sentry. Clearing the handle
    // also lets a re-login in the same tab start the stream again — the SDK
    // does not revive a listener it closed.
    if (this._deadAirSettingsStream) {
      this._deadAirSettingsStream.cancel()
      this._deadAirSettingsStream = undefined
    }
  }

  getPdfMaterialDownloadUrl = async (
    slideDeckId: string,
    material: SlideDeckMaterial
  ) => {
    const pdfRef = ref(
      this.storage,
      `slide_deck/${slideDeckId}/material/${material.id}.pdf`
    )
    const pdfUrl = await getDownloadURL(pdfRef)

    return pdfUrl
  }

  refreshAuthToken = async (forceRefresh = false) => {
    clearTimeout(this._authTokenRefreshTimeout)
    // if we're not authenticated, clear the token
    if (!this.currentUser) {
      this._authToken = null
      return
    }

    try {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV !== 'test') console.log('Fetching new AuthToken')

      const token = await this.currentUser.getIdToken(forceRefresh)

      // Decode the token to get the expiration time
      const { exp } = decodeJwt(token)
      const nowSeconds = DateTime.now().toSeconds()

      // Set the token if it's not expired
      if (exp && exp > nowSeconds) this._authToken = token

      // force refresh 10 mins before expiration
      const refreshAt = Math.max(exp ? exp - nowSeconds - 600 : 0, 0)
      this._authTokenRefreshTimeout = setTimeout(
        () => this.refreshAuthToken(true),
        refreshAt * 1000
      )
    } catch (e) {
      // if we fail to get the token, try again in 10 seconds
      this._authTokenRefreshTimeout = setTimeout(
        () => this.refreshAuthToken(true),
        10 * 1000
      )
    }
  }

  debug(
    category: string,
    message: string,
    // params to pass to analytics
    { params }: { params?: Record<string, unknown> } = {}
  ) {
    // do not spam the console in production
    if (!this.inProduction) {
      // eslint-disable-next-line no-console
      console.debug(`[${category}] ${message}`, params)
    }
    // Push to sentry so that we can pick it up there
    Sentry.addBreadcrumb({
      type: 'debug',
      message: message,
      category: category,
    })
    // Log to analytics as well
    const category_safe = category.replace(/\./g, '_').toLocaleLowerCase()
    this.logEvent(`debug_${category_safe}`, {
      message,
      ...params,
    })
  }

  async checkIsValidInvitation(invitationId: string) {
    const checkInvite = httpsCallable<
      { invitationId: string },
      CheckIsValidInvitationResult
    >(this.functions, 'checkIsValidInvitation')

    const { data } = await checkInvite({ invitationId })

    // if it's a section invitation, replace the data with the correct models
    if (data.isValid && data.invitationType === 'section') {
      // The backend returns timestamps as objects with _seconds field
      // and we need to convert them to Date objects
      const cleaned = { ...data.section }
      for (const key in cleaned) {
        if (key === 'sectionStartDate' || key === 'updatedAt') {
          const value = cleaned[key] as { _seconds?: number }
          const seconds = value._seconds
          if (seconds !== undefined) {
            cleaned[key] = new Date(seconds * 1000)
          }
        }
      }
      return {
        ...data,
        section: Section.empty(this).replaceData(cleaned),
        instructor: PublicUser.empty(this).replaceData(data.instructor),
      }
    }

    return data
  }

  // convenience function to ensure the required user data is loaded
  async waitForBreakoutUser(): Promise<BreakoutUser> {
    await when(
      () => {
        if (!this.breakoutUser) return false
        return this.breakoutUser.isLoaded
      },
      { timeout: 10_000 }
    )

    return this.breakoutUser!
  }

  get shouldTestCrashApp(): boolean {
    // NEVER allow crashing in production
    if (this.inProduction) return false

    return this._testCrashApp.get()
  }

  testCrashApp(): void {
    // NEVER allow crashing in production
    if (this.inProduction) return
    this._testCrashApp.set(true)
  }

  triggerOnZodError(): void {
    this.hasZodError = true
  }
}
